import { CHECKS, CHECK_BY_ID } from './checks';
import type { Bucket, CheckCtx, CheckDef, Outcome, Result, ResultsMap, Status } from './types';

/** Bumped ONLY on a breaking change to the window.ZW_CHECKLIST contract. */
export const CONTRACT_VERSION = 3;

export const RESUME_KEY = 'ZW_CHECKLIST_RESUME';

const DEFAULT_TIMEOUT_MS = 15000;
/** How many auto checks run at once. Keeps a full runAuto() inside ~60s. */
const CONCURRENCY = 4;

export interface RunnerParams {
  runner: boolean;
  autorun: boolean;
  /** Restrict every batch to these ids; empty means "no restriction". */
  checks: string[];
  skip: string[];
  timeoutMs: number;
}

interface ResumeRecord {
  id: string;
  phase: 'awaiting-return';
  startedTs: number;
  params: Record<string, string>;
  priorResults: ResultsMap;
}

/** Query params the runner owns; everything else is stripped on resume. */
const RUNNER_PARAMS = ['runner', 'autorun', 'checks', 'skip', 'timeout'];

export function parseParams(search: string): RunnerParams {
  const qs = new URLSearchParams(search);
  const list = (k: string) =>
    (qs.get(k) ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  const rawTimeout = Number(qs.get('timeout'));
  return {
    runner: qs.get('runner') === '1',
    autorun: qs.get('autorun') !== '0',
    checks: list('checks'),
    skip: list('skip'),
    timeoutMs: Number.isFinite(rawTimeout) && rawTimeout > 0 ? rawTimeout : DEFAULT_TIMEOUT_MS,
  };
}

function pending(def: CheckDef): Result {
  return { id: def.id, status: 'pending', detail: '', bucket: def.bucket, durationMs: 0, ts: 0 };
}

function freshResults(): ResultsMap {
  const map: ResultsMap = {};
  for (const def of CHECKS) map[def.id] = pending(def);
  return map;
}

export interface Engine {
  // ---- public contract (window.ZW_CHECKLIST) ----
  version: number;
  pageRevision: string;
  meta: Record<string, { title: string; bucket: Bucket; description: string }>;
  runAuto(): Promise<ResultsMap>;
  runAll(): Promise<ResultsMap>;
  run(id: string): Promise<Result>;
  results(): ResultsMap;
  reset(): Promise<void>;
  resumeState(): { id: string; phase: string } | null;
  onComplete(cb: (results: ResultsMap) => void): void;

  // ---- page-internal ----
  params: RunnerParams;
  /** Runs a check the way a human tapping its button would, manual included. */
  runFromUi(id: string, override?: (ctx: CheckCtx) => Promise<Outcome>): Promise<Result>;
  /** Overwrites just the detail of an already-settled check (live watchers). */
  patchDetail(id: string, detail: string): void;
  subscribe(cb: (results: ResultsMap) => void): () => void;
  /** True when this page load finished (or is still owed) a navigates check. */
  resumedThisLoad: boolean;
}

export function createEngine(win: Window = window): Engine {
  const params = parseParams(win.location.search);
  let results = freshResults();
  let resume: ResumeRecord | null = null;
  const subscribers = new Set<(r: ResultsMap) => void>();
  const completeListeners: ((r: ResultsMap) => void)[] = [];

  const snapshot = () => JSON.parse(JSON.stringify(results)) as ResultsMap;
  const notify = () => subscribers.forEach((cb) => cb(results));
  const fireComplete = () => completeListeners.forEach((cb) => cb(snapshot()));

  function put(id: string, patch: Partial<Result>) {
    const prev = results[id];
    if (!prev) return;
    results = { ...results, [id]: { ...prev, ...patch } };
    notify();
  }

  function record(id: string, outcome: Outcome, startedAt: number) {
    put(id, {
      status: outcome.status,
      detail: outcome.detail,
      durationMs: Math.max(0, Math.round(performance.now() - startedAt)),
      ts: Date.now(),
    });
  }

  const timeoutFor = (def: CheckDef) =>
    def.bucket === 'navigates' ? params.timeoutMs * 2 : params.timeoutMs;

  function ctxFor(def: CheckDef): CheckCtx {
    return {
      timeoutMs: timeoutFor(def),
      runner: params.runner,
      el: <T extends HTMLElement>(id: string) => win.document.getElementById(id) as T | null,
      leave: (url: string, extra: Record<string, string>) => {
        writeResume({
          id: def.id,
          phase: 'awaiting-return',
          startedTs: Date.now(),
          params: extra,
          priorResults: snapshot(),
        });
        win.location.href = url;
      },
    };
  }

  function writeResume(rec: ResumeRecord) {
    resume = rec;
    try {
      win.sessionStorage.setItem(RESUME_KEY, JSON.stringify(rec));
    } catch {
      /* sessionStorage can be off; the round trip then simply reports nothing */
    }
  }

  function clearResume() {
    resume = null;
    try {
      win.sessionStorage.removeItem(RESUME_KEY);
    } catch { /* nothing to clear */ }
  }

  /**
   * Runs one check under the per-check timeout. The timeout is what guarantees
   * runAuto() always settles: a check that hangs is force-failed.
   */
  async function execute(def: CheckDef, body: () => Promise<Outcome>, ms: number): Promise<Result> {
    const startedAt = performance.now();
    put(def.id, { status: 'running', detail: '', durationMs: 0, ts: 0 });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const outcome = await Promise.race<Outcome>([
      body().catch((e) => ({ status: 'fail' as const, detail: String(e?.message || e) })),
      new Promise<Outcome>((resolve) => {
        timer = setTimeout(() => resolve({ status: 'fail', detail: `timeout after ${ms}ms` }), ms);
      }),
    ]);
    clearTimeout(timer);
    // A navigates check that timed out never left the page; drop the resume
    // record so the next load does not try to score a trip that never happened.
    if (def.bucket === 'navigates' && resume?.id === def.id) clearResume();
    record(def.id, outcome, startedAt);
    return results[def.id];
  }

  function selected(buckets: Bucket[]): CheckDef[] {
    return CHECKS.filter((def) => {
      if (!buckets.includes(def.bucket)) return false;
      if (params.checks.length && !params.checks.includes(def.id)) return false;
      if (params.skip.includes(def.id)) return false;
      return true;
    });
  }

  async function runBatch(defs: CheckDef[]): Promise<ResultsMap> {
    const queue = [...defs];
    const worker = async () => {
      for (;;) {
        const def = queue.shift();
        if (!def) return;
        await execute(def, () => def.run(ctxFor(def)), timeoutFor(def));
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
    fireComplete();
    return snapshot();
  }

  async function run(id: string): Promise<Result> {
    const def = CHECK_BY_ID[id];
    if (!def) throw new Error(`unknown check id: ${id}`);
    // A manual check cannot be driven from JS at all; say so straight away
    // rather than hanging until the timeout (contract §6).
    if (def.bucket === 'manual') {
      record(id, { status: 'skip', detail: 'manual check' }, performance.now());
      fireComplete();
      return results[id];
    }
    const result = await execute(def, () => def.run(ctxFor(def)), timeoutFor(def));
    fireComplete();
    return result;
  }

  // ---- resume protocol -------------------------------------------------
  // A `navigates` check unloads the page, so its verdict is recorded by the
  // page load that comes back, from a record left in sessionStorage.

  function readResume(): ResumeRecord | null {
    try {
      const raw = win.sessionStorage.getItem(RESUME_KEY);
      if (!raw) return null;
      const rec = JSON.parse(raw) as ResumeRecord;
      return rec && typeof rec.id === 'string' ? rec : null;
    } catch {
      return null;
    }
  }

  function stripLandingParams() {
    const url = new URL(win.location.href);
    const keep = new URLSearchParams();
    for (const key of RUNNER_PARAMS) {
      const v = url.searchParams.get(key);
      if (v !== null) keep.set(key, v);
    }
    const qs = keep.toString();
    win.history.replaceState(null, '', `${url.pathname}${qs ? `?${qs}` : ''}`);
  }

  let resumedThisLoad = false;

  function processResume() {
    const rec = readResume();
    if (!rec) return;
    resume = rec;
    const def = CHECK_BY_ID[rec.id];
    if (!def?.resume) return clearResume();

    // Results gathered before the navigation travel in the record.
    results = { ...freshResults(), ...(rec.priorResults || {}) };

    const elapsedMs = Date.now() - rec.startedTs;
    const query = new URLSearchParams(win.location.search);
    const outcome = def.resume({ query, params: rec.params || {}, elapsedMs });
    const landed = outcome.status !== 'fail' || outcome.detail !== 'did not return';

    if (!landed && elapsedMs <= timeoutFor(def)) {
      // Still inside the window: this load is some other navigation, keep
      // waiting. resumeState() reports the check as outstanding.
      put(rec.id, { status: 'running', detail: 'awaiting return' });
      return;
    }

    resumedThisLoad = true;
    put(rec.id, {
      status: landed ? outcome.status : 'fail',
      detail: landed ? outcome.detail : `did not return within ${timeoutFor(def)}ms`,
      durationMs: elapsedMs,
      ts: Date.now(),
    });
    clearResume();
    if (landed) stripLandingParams();
    fireComplete();
  }

  processResume();

  // ---- cleanup ---------------------------------------------------------

  async function clearOriginState() {
    for (const c of win.document.cookie.split('; ')) {
      const name = c.split('=')[0];
      if (name.startsWith('zw')) win.document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
    }
    try {
      win.localStorage.removeItem('zw_probe');
      win.sessionStorage.removeItem('zw_probe');
    } catch { /* storage may be disabled */ }
    try {
      win.indexedDB?.deleteDatabase('zw-test');
    } catch { /* no IndexedDB */ }
    if ('caches' in win) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.filter((k) => k.startsWith('zw-')).map((k) => caches.delete(k)));
      } catch { /* Cache Storage may be unavailable */ }
    }
  }

  const engine: Engine = {
    version: CONTRACT_VERSION,
    pageRevision:
      (import.meta.env.VITE_BUILD_ID as string | undefined) ||
      (import.meta.env.VITE_GIT_SHA as string | undefined) ||
      'dev',
    meta: Object.fromEntries(
      CHECKS.map((def) => [
        def.id,
        { title: def.title, bucket: def.bucket, description: def.description },
      ]),
    ),

    runAuto: () => runBatch(selected(['auto'])),

    async runAll() {
      const map = await runBatch(selected(['auto', 'permission']));
      // The first leg of a `navigates` check unloads the page, so it goes last
      // and after this promise has already handed its results back.
      const nav = selected(['navigates'])[0];
      if (nav) setTimeout(() => void run(nav.id), 0);
      return map;
    },

    run,
    results: snapshot,

    async reset() {
      clearResume();
      await clearOriginState();
      results = freshResults();
      notify();
    },

    resumeState: () => (resume ? { id: resume.id, phase: resume.phase } : null),

    onComplete(cb) {
      completeListeners.push(cb);
    },

    params,
    resumedThisLoad,

    async runFromUi(id, override) {
      const def = CHECK_BY_ID[id];
      if (!def) throw new Error(`unknown check id: ${id}`);
      const body = override ? () => override(ctxFor(def)) : () => def.run(ctxFor(def));
      const result = await execute(def, body, timeoutFor(def));
      fireComplete();
      return result;
    },

    patchDetail(id, detail) {
      put(id, { detail });
    },

    subscribe(cb) {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
  };

  return engine;
}

export type { Result, ResultsMap, Status, Bucket };
